/**
 * WhatEricIsDoingPanel (PR-09).
 *
 * Renders ONLY when status is one of {pulled, in_progress, agent_draft,
 * eric_reviewing} (PR-09 / ui-structure.md §states). It references at least
 * one concrete detail from the subscriber's context doc so the panel reads as
 * "Eric is actually working on YOUR thing" rather than a generic spinner.
 *
 * The copy is present-tense, plain, honest (design-guidelines §10 portal
 * status tone). It never exposes operator workflow vocabulary (agent_draft /
 * eric_reviewing are internal); the subscriber sees a human sentence.
 */

import type { ContextDocSectionsPayload, TaskStatus } from '@studio/api-schemas';

const VISIBLE_STATUSES: ReadonlyArray<TaskStatus> = [
  'pulled',
  'in_progress',
  'agent_draft',
  'eric_reviewing',
];

function isWhatEricIsDoingVisible(status: TaskStatus): boolean {
  return VISIBLE_STATUSES.includes(status);
}

/**
 * Pull one concrete context atom to ground the panel (PR-09 "references >= 1
 * context-atom detail"). Prefers the company one-liner / what-they-sell, then
 * the biggest blocker, then the primary buyer.
 */
function pickContextDetail(
  sections: ContextDocSectionsPayload | null,
): string | null {
  if (!sections) return null;
  const company = sections.company;
  if (company?.what_they_sell) return company.what_they_sell;
  const strat = sections.strategic_context;
  if (strat?.biggest_blocker) return strat.biggest_blocker;
  if (strat?.current_one_liner) return strat.current_one_liner;
  const aud = sections.audiences;
  if (aud?.primary_buyer) return `your primary buyer (${aud.primary_buyer})`;
  if (company?.name) return company.name;
  return null;
}

export function WhatEricIsDoingPanel({
  status,
  contextSections,
}: {
  status: TaskStatus;
  contextSections: ContextDocSectionsPayload | null;
}) {
  if (!isWhatEricIsDoingVisible(status)) return null;

  const detail = pickContextDetail(contextSections);

  const headline =
    status === 'pulled'
      ? 'Eric is reading your task brief.'
      : 'Eric is working on this now.';

  const grounding = detail
    ? `He has your context open, including ${detail}. The deliverable lands here when it is ready.`
    : 'He has your context doc open. The deliverable lands here when it is ready.';

  return (
    <section
      className="rounded-lg border-l-2 border-l-[color:var(--color-rose-deep)] border-y border-r border-y-[color:var(--color-rule)] border-r-[color:var(--color-rule)] bg-[color:var(--color-white)] p-5 lg:p-6"
      aria-label="What Eric is doing now"
    >
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
        What Eric is doing right now
      </p>
      <p className="font-display text-lg italic leading-snug text-[color:var(--color-black)]">
        {headline}
      </p>
      <p className="mt-2 text-sm leading-relaxed text-[color:var(--color-text-soft)]">
        {grounding}
      </p>
    </section>
  );
}
